import { Injectable, OnDestroy } from '@angular/core';
import { ENDPOINTS } from 'app/core/constants/endpoints';
import { ApiService } from 'app/core/services/api.service';
import { NotificationResponseModel } from 'app/modules/home/models/notifications-response.model';
import { LoggerService } from 'app/shared/services/logger.service';
import { environment } from 'environments/environment';
import type Echo from 'laravel-echo';
import { BehaviorSubject, Subject } from 'rxjs';

export interface AgentNotificationEvent {
  notification: NotificationResponseModel | null;
  unread_count: number;
}

export interface UnreadCountEvent {
  unread_count: number;
}

/**
 * Single Reverb (WebSocket) connection for the logged-in agent.
 *
 * Listens on the private channel App.Models.Agent.{id}, which salis-be pushes
 * to whenever an AgentNotification is created or the unread count changes.
 * Channel auth goes through ApiService, so the AuthInterceptor's bearer token
 * and refresh-on-401 logic apply exactly like any other API call.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService implements OnDestroy {
  private echo: Echo<'reverb'> | null = null;
  private agentId: number | null = null;
  private hasConnectedBefore = false;

  /** A new notification was stored for this agent. */
  readonly notificationCreated$ = new Subject<AgentNotificationEvent>();
  /** Unread count changed (mark read / mark all / delete, possibly from another tab). */
  readonly unreadCountChanged$ = new Subject<UnreadCountEvent>();
  /** Socket came back after a drop; events may have been missed, so re-fetch over REST. */
  readonly reconnected$ = new Subject<void>();
  readonly connected$ = new BehaviorSubject<boolean>(false);

  constructor(
    private apiService: ApiService,
    private logger: LoggerService
  ) {}

  async connect(agentId: number | undefined): Promise<void> {
    if (!environment.reverb?.key || !agentId || this.agentId === agentId) {
      return;
    }
    this.disconnect();
    this.agentId = agentId;

    // Lazy-loaded so the socket libraries stay out of the initial bundle
    let EchoClient: typeof Echo;
    let Pusher: typeof import('pusher-js').default;
    try {
      [{ default: EchoClient }, { default: Pusher }] = await Promise.all([
        import('laravel-echo'),
        import('pusher-js'),
      ]);
    } catch (error) {
      this.agentId = null; // allow a later retry; polling fallback covers us meanwhile
      this.logger.error('Realtime', 'Failed to load socket client', error);
      return;
    }
    if (this.agentId !== agentId) {
      return; // disconnected (logout) while the chunks were loading
    }

    const { key, host, port, scheme } = environment.reverb;

    this.echo = new EchoClient({
      broadcaster: 'reverb',
      key,
      wsHost: host,
      wsPort: port,
      wssPort: port,
      forceTLS: scheme === 'https',
      enabledTransports: ['ws', 'wss'],
      Pusher,
      channelAuthorization: {
        endpoint: '',
        transport: 'ajax',
        customHandler: (
          params: { socketId: string; channelName: string },
          callback: (error: Error | null, data: any) => void
        ) => {
          this.apiService
            .post<{ auth: string }>(ENDPOINTS.BROADCASTING.AUTH, {
              socket_id: params.socketId,
              channel_name: params.channelName,
            })
            .subscribe({
              next: (data) => callback(null, data),
              error: (error) => callback(error, null),
            });
        },
      },
    });

    const pusher = this.echo.connector.pusher;
    pusher.connection.bind('state_change', (states: { current: string }) => {
      const isConnected = states.current === 'connected';
      this.connected$.next(isConnected);
      if (isConnected) {
        if (this.hasConnectedBefore) {
          this.reconnected$.next();
        }
        this.hasConnectedBefore = true;
      }
    });

    this.echo
      .private(`App.Models.Agent.${agentId}`)
      .notification((event: AgentNotificationEvent) =>
        this.notificationCreated$.next(event)
      )
      .listen('.unread-count.updated', (event: UnreadCountEvent) =>
        this.unreadCountChanged$.next(event)
      )
      .error((error: unknown) =>
        this.logger.error('Realtime', 'Agent channel subscription failed', error)
      );
  }

  disconnect(): void {
    this.echo?.disconnect();
    this.echo = null;
    this.agentId = null;
    this.hasConnectedBefore = false;
    this.connected$.next(false);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
